import { Alarm } from '@akin-home/alarms';
import {
  alarmModelToCreateGraphql,
  alarmModelToUpdateData,
  createAlarmMutation,
  deleteAlarmMutation,
  getAlarmQuery,
  listAllAlarmsByHabitatQuery,
  updateAlarmMutation,
} from '@akin-home/alarms/frontend';
import { startDateToDateObject } from '@akin-home/calendar';
import {
  createCreateMutationHook,
  createOptimisticUpdateMutationHook,
  createQueryKeySet,
} from '@akin-home/utils/react-query';
import { useCallback } from 'react';
import {
  UseQueryOptions,
  useMutation,
  useQuery,
  useQueryClient,
} from 'react-query';

export * from './useGetAlarm';

export const alarmQueryKeys = createQueryKeySet({
  key: 'alarms',
});

export const useListAllAlarmsByHabitatQuery = (params: {
  habitatID: string;
  filter?: { from?: string; to?: string };
}) =>
  useQuery(
    alarmQueryKeys.list(params),
    async () =>
      (await listAllAlarmsByHabitatQuery(params.habitatID, params.filter)).map(
        startDateToDateObject
      ),
    {
      enabled: !!params?.habitatID,
    }
  );

export const useDeleteAlarm = () => {
  const queryClient = useQueryClient();

  return useMutation(deleteAlarmMutation, {
    onSuccess() {
      // Since we are dealing with recurring items etc, just invalidate the queries
      queryClient.invalidateQueries<Alarm[]>(alarmQueryKeys.all);
    },
  });
};

export const useCreateAlarm = createCreateMutationHook({
  keySet: alarmQueryKeys,
  mutationFunc: createAlarmMutation,
});

export const useUpdateAlarm = createOptimisticUpdateMutationHook({
  keySet: alarmQueryKeys,
  mutationFunc: updateAlarmMutation,
  keyExtractor: (vars) => vars.input?.id,
  updater: ({ variables, item }) => {
    return {
      ...item,
      ...variables.input,
      start: variables.input?.start
        ? new Date(variables.input.start)
        : item.start,
    };
  },
});

export const useUpdateAlarmFromModel = () => {
  const update = useUpdateAlarm();

  return useCallback(
    async (alarm: Alarm) =>
      update.mutateAsync({ input: await alarmModelToUpdateData(alarm) }),
    [update]
  );
};

export const useCreateAlarmFromModel = () => {
  const createEvent = useCreateAlarm();

  return useCallback(
    async (event: Alarm) =>
      createEvent.mutateAsync({
        input: await alarmModelToCreateGraphql(event),
      }),
    [createEvent]
  );
};

export const useGetAlarmQuery = (
  id: string | undefined,
  options?: UseQueryOptions
) =>
  useQuery(
    alarmQueryKeys.detail(id),
    async () => startDateToDateObject(await getAlarmQuery(id as string)),
    {
      enabled: !!id,
      ...options,
    } as any
  );
